import React from "react";
import { CustomCheckBox } from "./CustomCheckBox";
import styles from "./MultiSelect.module.css";

// Multi-value picker rendered as a checkbox list. Reuses the local
// CustomCheckBox (the dominant local convention) so it visually matches the
// rest of the app. Values not present in `options` are preserved (shown as a
// "not found" chip) rather than silently dropped — the same anti-typo stance as
// Select. Built for the stream-profile track pickers.

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectProps {
  values: string[];
  options: MultiSelectOption[];
  onValues?: (values: string[]) => void;
  disabled?: boolean;
  emptyLabel?: string;
  className?: string;
}

const MultiSelect = ({ values, options, onValues, disabled, emptyLabel, className }: MultiSelectProps) => {
  const selected = values ?? [];
  const toggle = (value: string, on: boolean) => {
    const next = on
      ? (selected.includes(value) ? selected : [...selected, value])
      : selected.filter((current) => current !== value);
    onValues?.(next);
  };

  // Any selected value that is not an offered option (stale reference).
  const orphans = selected.filter((value) => !options.some((option) => option.value === value));

  const classes = [styles.container];
  if (className) classes.push(className);

  return (
    <div className={classes.join(" ")}>
      {options.length === 0 && orphans.length === 0 ? (
        <span className={styles.empty}>{emptyLabel ?? "—"}</span>
      ) : null}
      {options.map((option) => (
        <label key={option.value} className={styles.item}>
          <CustomCheckBox
            isSelected={selected.includes(option.value)}
            setSelected={(on) => toggle(option.value, on)}
            disabled={disabled}
          />
          <span>{option.label}</span>
        </label>
      ))}
      {orphans.map((value) => (
        <label key={value} className={[styles.item, styles.orphan].join(" ")}>
          <CustomCheckBox isSelected setSelected={(on) => toggle(value, on)} disabled={disabled} />
          <span>{`${value} — not found ⚠`}</span>
        </label>
      ))}
    </div>
  );
};

export default MultiSelect;
